import { useStore } from '~/composables/useStore';
import { LSCustomerDefaultsSchema, type LSCustomerDefaults } from '#shared/types/defaults';

// useSessionGuard is auto-imported from the auth layer (importing a layer's
// app/** internals directly is blocked by the cross-layer lint rule).

const KEY = 'lcp-user-defaults';

interface UseDefaultsStoreOptions {
	transform?: (defaults: LSCustomerDefaults) => LSCustomerDefaults;
}

export function useDefaultsStore(options?: UseDefaultsStoreOptions) {
	const { transform } = options || {};
	const { $api } = useNuxtApp();
	const { portalType } = useCustomerConfig();
	const { hasSession } = useSessionGuard();

	const skipDefaults = computed(
		() => portalType.value === 'mapping' || portalType.value === 'supplier',
	);

	const emptyDefaults = (): LSCustomerDefaults => {
		const fallback = LSCustomerDefaultsSchema.parse({}) as LSCustomerDefaults;
		return transform ? transform(fallback) : fallback;
	};

	const {
		data,
		loading,
		refresh: _refresh,
	} = useStore<LSCustomerDefaults>({
		key: KEY,
		server: false,
		immediate: false,
		fetcher: async () => {
			if (skipDefaults.value) {
				frogger.debug('defaults fetch skipped — unsupported portal type', {
					portalType: portalType.value,
				});
				return null as unknown as LSCustomerDefaults;
			}

			// Never call the authenticated endpoint without a confirmed session.
			// refresh() is only fired from the login-gated watcher, but this makes
			// a token-less GetUserDefaults impossible regardless of the caller.
			if (!hasSession.value) {
				frogger.debug('defaults fetch skipped - no active session');
				return emptyDefaults();
			}

			try {
				const validated = await $api('/api/Auth/GetUserDefaults', {
					schema: LSCustomerDefaultsSchema,
				});
				frogger.debug('defaults store loaded');
				return transform ? transform(validated) : validated;
			} catch (error) {
				// Resilience: never break the page on a bad/missing defaults payload.
				frogger.warn('defaults load failed — using empty defaults', error);
				return emptyDefaults();
			}
		},
	});

	function refresh() {
		if (skipDefaults.value) {
			frogger.debug('defaults refresh skipped — unsupported portal type', {
				portalType: portalType.value,
			});
			return Promise.resolve();
		}
		return _refresh();
	}

	return {
		data,
		loading,
		fetching: loading,
		refresh,
	};
}
